import { apiClient } from '@/shared/api';
import type {
  AttributeCreate,
  AttributeDeleteParams,
  AttributeFetchOptions,
  AttributeGetParams,
  AttributeListParams,
  AttributeListResponse,
  AttributeNameListParams,
  AttributeResponse,
  AttributeRestoreParams,
  AttributeStructListParams,
  AttributeStructListResponse,
  AttributeUpdate,
  AttributeUpdateParams,
  AttributeWithValuesListResponse,
} from './types';

/**
 * GET /attribute - list attributes with values per OpenAPI GET /attribute
 */
export const fetchAttributes = async (params?: AttributeListParams): Promise<AttributeWithValuesListResponse> => {
  const { data } = await apiClient.get<AttributeWithValuesListResponse>('/attribute', { params });
  return data;
};

/**
 * GET /attribute-name - list attributes without values per OpenAPI GET /attribute-name
 */
export const fetchAttributeNames = async (params?: AttributeNameListParams): Promise<AttributeListResponse> => {
  const { data } = await apiClient.get<AttributeListResponse>('/attribute-name', { params });
  return data;
};

/**
 * GET /attribute/{code} - get attribute by code per OpenAPI GET /attribute/{code}
 *
 * `silentError` подавляет всплывающее уведомление об ошибке — нужен вызывающим,
 * для которых 404 является штатным ответом («атрибут не заведён») и показывается в их UI.
 */
export const fetchAttributeByCode = async (
  code: string,
  params?: AttributeGetParams,
  options?: AttributeFetchOptions,
): Promise<AttributeResponse> => {
  const { data } = await apiClient.get<AttributeResponse>(`/attribute/${code}`, {
    params,
    silentError: options?.silentError,
  });
  return data;
};

/**
 * POST /attribute - create attribute per OpenAPI POST /attribute
 */
export const createAttribute = async (attribute: AttributeCreate): Promise<AttributeResponse> => {
  const { data } = await apiClient.post<AttributeResponse>('/attribute', attribute);
  return data;
};

/**
 * PUT /attribute/{code} - update attribute per OpenAPI PUT /attribute/{code}
 */
export const updateAttribute = async ({
  code,
  attribute,
  params,
}: {
  code: string;
  attribute: AttributeUpdate;
  params?: AttributeUpdateParams;
}): Promise<AttributeResponse> => {
  const { data } = await apiClient.put<AttributeResponse>(`/attribute/${code}`, attribute, { params });
  return data;
};

/**
 * DELETE /attribute/{code} - soft delete attribute per OpenAPI DELETE /attribute/{code}
 */
export const deleteAttribute = async (code: string, params?: AttributeDeleteParams): Promise<void> => {
  await apiClient.delete(`/attribute/${code}`, { params });
};

/**
 * POST /attribute/{code}/restore - restore soft-deleted attribute per OpenAPI POST /attribute/{code}/restore
 */
export const restoreAttribute = async (code: string, params?: AttributeRestoreParams): Promise<void> => {
  await apiClient.post(`/attribute/${code}/restore`, undefined, { params });
};

/**
 * GET /attribute-struct - list attribute structs per OpenAPI GET /attribute-struct
 */
export const fetchAttributeStructs = async (
  params?: AttributeStructListParams,
): Promise<AttributeStructListResponse> => {
  const { data } = await apiClient.get<AttributeStructListResponse>('/attribute-struct', { params });
  return data;
};

/**
 * GET /attribute/search-reference - search reference attributes for rule field
 */
export const searchAttributeReferences = async (query: string, limit = 10): Promise<AttributeResponse[]> => {
  const { data } = await apiClient.get<AttributeResponse[]>('/attribute/search-reference', {
    params: { query, limit },
  });
  return data;
};
